import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { BigqueryConfig } from '@/api/types';
import { REDACTION_SENTINEL } from '@/api/types';
import { Field, FormShell } from './shared';

interface Props {
  initial: BigqueryConfig | null;
  onSubmit: (config: Partial<BigqueryConfig>) => void;
  submitting: boolean;
  onCancel: () => void;
  onReset?: () => void;
}

export function BigqueryForm({ initial, onSubmit, submitting, onCancel, onReset }: Props): JSX.Element {
  const { t } = useTranslation();
  const [projectId, setProjectId] = React.useState(initial?.projectId ?? '');
  const [keyJson, setKeyJson] = React.useState(initial?.keyJson ?? '');
  const [location, setLocation] = React.useState(initial?.location ?? 'EU');
  const [maxBytesBilled, setMaxBytesBilled] = React.useState(
    String(initial?.maxBytesBilled ?? 1_000_000_000),
  );
  const [defaultLimit, setDefaultLimit] = React.useState(String(initial?.defaultLimit ?? 1000));
  const [timeoutMs, setTimeoutMs] = React.useState(String(initial?.timeoutMs ?? 30_000));

  return (
    <form
      id="integration-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          projectId: projectId.trim(),
          keyJson: keyJson === REDACTION_SENTINEL || !keyJson ? REDACTION_SENTINEL : keyJson,
          location: location.trim(),
          maxBytesBilled: Number(maxBytesBilled),
          defaultLimit: Number(defaultLimit),
          timeoutMs: Number(timeoutMs),
        });
      }}
    >
      <FormShell submitting={submitting} onCancel={onCancel} onReset={onReset}>
        <Field label={t('integrations.form.bigquery.projectId')} htmlFor="bq-pid" required>
          <Input
            id="bq-pid"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            required
          />
        </Field>
        <Field
          label={t('integrations.form.bigquery.keyJson')}
          htmlFor="bq-key"
          required
          hint={t('integrations.form.bigquery.keyJsonHint')}
        >
          <Textarea
            id="bq-key"
            value={keyJson}
            onChange={(e) => setKeyJson(e.target.value)}
            placeholder="{...}"
            rows={6}
            className="font-mono text-[11px]"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('integrations.form.bigquery.location')} htmlFor="bq-loc" required>
            <Input id="bq-loc" value={location} onChange={(e) => setLocation(e.target.value)} required />
          </Field>
          <Field label={t('integrations.form.bigquery.defaultLimit')} htmlFor="bq-lim" required>
            <Input
              id="bq-lim"
              type="number"
              value={defaultLimit}
              onChange={(e) => setDefaultLimit(e.target.value)}
              required
            />
          </Field>
          <Field label={t('integrations.form.bigquery.maxBytesBilled')} htmlFor="bq-bytes" required>
            <Input
              id="bq-bytes"
              type="number"
              value={maxBytesBilled}
              onChange={(e) => setMaxBytesBilled(e.target.value)}
              required
            />
          </Field>
          <Field label={t('integrations.form.bigquery.timeoutMs')} htmlFor="bq-to" required>
            <Input
              id="bq-to"
              type="number"
              value={timeoutMs}
              onChange={(e) => setTimeoutMs(e.target.value)}
              required
            />
          </Field>
        </div>
      </FormShell>
    </form>
  );
}
